<template>
  <div class="attendance-details">
    <!-- Summary Cards -->
    <div class="row mb-4">
      <div class="col-lg-3 col-md-6 mb-3">
        <div class="card stats-card">
          <div class="card-body text-center">
            <feather-icon icon="CalendarIcon" size="24" class="text-primary mb-2" />
            <h4 class="mb-1">{{ attendanceData.total_lessons || 0 }}</h4>
            <small class="text-muted">Всего уроков</small>
          </div>
        </div>
      </div>
      <div class="col-lg-3 col-md-6 mb-3">
        <div class="card stats-card">
          <div class="card-body text-center">
            <feather-icon icon="CheckCircleIcon" size="24" class="text-success mb-2" />
            <h4 class="mb-1">{{ attendanceData.attended || 0 }}</h4>
            <small class="text-muted">Посещено</small>
          </div>
        </div>
      </div>
      <div class="col-lg-3 col-md-6 mb-3">
        <div class="card stats-card">
          <div class="card-body text-center">
            <feather-icon icon="XCircleIcon" size="24" class="text-warning mb-2" />
            <h4 class="mb-1">{{ attendanceData.missed || 0 }}</h4>
            <small class="text-muted">Пропущено</small>
          </div>
        </div>
      </div>
      <div class="col-lg-3 col-md-6 mb-3">
        <div class="card stats-card">
          <div class="card-body text-center">
            <feather-icon icon="TrendingUpIcon" size="24" class="text-info mb-2" />
            <h4 class="mb-1">{{ attendanceData.attendance_rate || 0 }}%</h4>
            <small class="text-muted">Посещаемость</small>
          </div>
        </div>
      </div>
    </div>

    <!-- Attendance Progress -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="card">
          <div class="card-header">
            <h5 class="mb-0">Прогресс посещаемости</h5>
          </div>
          <div class="card-body">
            <div class="attendance-progress mb-3">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <span>Общая посещаемость:</span>
                <span class="font-weight-bold" :class="getAttendanceClass(attendanceData.attendance_rate)">
                  {{ attendanceData.attendance_rate || 0 }}%
                </span>
              </div>
              <b-progress
                :value="attendanceData.attendance_rate || 0"
                :variant="getProgressVariant(attendanceData.attendance_rate || 0)"
                height="10px"
              />
            </div>

            <!-- Attendance Distribution -->
            <div class="row">
              <div class="col-md-6">
                <canvas ref="attendanceChart" width="300" height="200"></canvas>
              </div>
              <div class="col-md-6">
                <div class="attendance-legend">
                  <div class="legend-item d-flex align-items-center mb-2">
                    <div class="legend-color bg-success mr-2"></div>
                    <span>Посещено: {{ attendanceData.attended || 0 }} уроков</span>
                  </div>
                  <div class="legend-item d-flex align-items-center mb-2">
                    <div class="legend-color bg-warning mr-2"></div>
                    <span>Пропущено: {{ attendanceData.missed || 0 }} уроков</span>
                  </div>
                  <div class="legend-item d-flex align-items-center">
                    <div class="legend-color bg-secondary mr-2"></div>
                    <span>Отменено: {{ getCancelledCount() }} уроков</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Detailed Lessons List -->
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5 class="mb-0">История посещений</h5>
            <div class="d-flex align-items-center">
              <!-- Filter -->
              <b-form-select
                v-model="statusFilter"
                :options="filterOptions"
                size="sm"
                class="mr-2"
                style="width: auto;"
              />
              <!-- Search -->
              <b-input-group size="sm" style="width: 200px;">
                <b-form-input
                  v-model="searchQuery"
                  placeholder="Поиск уроков..."
                />
                <b-input-group-append>
                  <b-button variant="outline-secondary" size="sm">
                    <feather-icon icon="SearchIcon" size="14" />
                  </b-button>
                </b-input-group-append>
              </b-input-group>
            </div>
          </div>
          <div class="card-body p-0">
            <!-- Lessons Table -->
            <div class="table-responsive">
              <table class="table table-hover mb-0">
                <thead class="thead-light">
                  <tr>
                    <th>Дата</th>
                    <th>Урок</th>
                    <th>Тип</th>
                    <th>Статус</th>
                    <th>Длительность</th>
                    <th>Заметки</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="lesson in filteredLessons"
                    :key="lesson.id"
                    class="lesson-row"
                  >
                    <td>
                      <div>
                        <strong>{{ formatDate(lesson.scheduled_at) }}</strong>
                        <br>
                        <small class="text-muted">{{ formatTime(lesson.scheduled_at) }}</small>
                      </div>
                    </td>
                    <td>
                      <div>
                        <strong>{{ lesson.lesson_title }}</strong>
                        <br>
                        <small class="text-muted">{{ lesson.lesson_type || 'Обычный урок' }}</small>
                      </div>
                    </td>
                    <td>
                      <b-badge :variant="getLessonTypeVariant(lesson.lesson_type)">
                        {{ getLessonTypeText(lesson.lesson_type) }}
                      </b-badge>
                    </td>
                    <td>
                      <b-badge :variant="getStatusVariant(lesson.status)">
                        {{ getStatusText(lesson.status) }}
                      </b-badge>
                    </td>
                    <td>
                      <span v-if="lesson.duration_minutes">
                        {{ formatDuration(lesson.duration_minutes) }}
                      </span>
                      <span v-else class="text-muted">-</span>
                    </td>
                    <td>
                      <span v-if="lesson.notes" class="text-truncate" style="max-width: 200px;">
                        {{ lesson.notes }}
                      </span>
                      <span v-else class="text-muted">-</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- No Results -->
            <div v-if="filteredLessons.length === 0" class="text-center py-5">
              <feather-icon icon="CalendarIcon" size="48" class="text-muted mb-3" />
              <h5 class="text-muted">Уроки не найдены</h5>
              <p class="text-muted">Попробуйте изменить фильтры поиска</p>
            </div>

            <!-- Pagination -->
            <div v-if="filteredLessons.length > itemsPerPage" class="p-3 border-top">
              <b-pagination
                v-model="currentPage"
                :total-rows="filteredLessons.length"
                :per-page="itemsPerPage"
                align="center"
                size="sm"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  BProgress,
  BBadge,
  BFormSelect,
  BFormInput,
  BInputGroup,
  BInputGroupAppend,
  BButton,
  BPagination,
  BCard,
  BCardHeader,
  BCardBody
} from 'bootstrap-vue'

export default {
  name: 'AttendanceDetails',
  components: {
    BProgress,
    BBadge,
    BFormSelect,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
    BButton,
    BPagination,
    BCard,
    BCardHeader,
    BCardBody
  },
  props: {
    attendanceData: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      statusFilter: 'all',
      searchQuery: '',
      currentPage: 1,
      itemsPerPage: 10,
      filterOptions: [
        { value: 'all', text: 'Все уроки' },
        { value: 'attended', text: 'Посещенные' },
        { value: 'missed', text: 'Пропущенные' },
        { value: 'cancelled', text: 'Отмененные' },
        { value: 'scheduled', text: 'Запланированные' }
      ]
    }
  },
  computed: {
    lessons() {
      return this.attendanceData.lessons || []
    },
    filteredLessons() {
      let filtered = this.lessons

      // Filter by status
      if (this.statusFilter !== 'all') {
        filtered = filtered.filter(lesson => lesson.status === this.statusFilter)
      }

      // Filter by search query
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        filtered = filtered.filter(lesson =>
          lesson.lesson_title.toLowerCase().includes(query) ||
          (lesson.notes && lesson.notes.toLowerCase().includes(query))
        )
      }

      return filtered
    }
  },
  methods: {
    getAttendanceClass(rate) {
      if (rate >= 90) return 'text-success'
      if (rate >= 70) return 'text-warning'
      return 'text-danger'
    },
    getProgressVariant(rate) {
      if (rate >= 90) return 'success'
      if (rate >= 70) return 'warning'
      return 'danger'
    },
    getStatusVariant(status) {
      const variants = {
        attended: 'success',
        missed: 'warning',
        cancelled: 'secondary',
        scheduled: 'primary',
        rescheduled: 'info'
      }
      return variants[status] || 'secondary'
    },
    getStatusText(status) {
      const texts = {
        attended: 'Посещен',
        missed: 'Пропущен',
        cancelled: 'Отменен',
        scheduled: 'Запланирован',
        rescheduled: 'Перенесен'
      }
      return texts[status] || status
    },
    getLessonTypeVariant(type) {
      const variants = {
        regular: 'primary',
        webinar: 'info',
        control: 'warning',
        individual: 'success'
      }
      return variants[type] || 'secondary'
    },
    getLessonTypeText(type) {
      const texts = {
        regular: 'Обычный',
        webinar: 'Вебинар',
        control: 'Контрольный',
        individual: 'Индивидуальный'
      }
      return texts[type] || 'Урок'
    },
    getCancelledCount() {
      return this.lessons.filter(lesson => lesson.status === 'cancelled').length
    },
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    },
    formatTime(dateString) {
      return new Date(dateString).toLocaleTimeString('ru-RU', {
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    formatDuration(minutes) {
      const hours = Math.floor(minutes / 60)
      const mins = minutes % 60

      if (hours > 0) {
        return mins > 0 ? `${hours}ч ${mins}м` : `${hours}ч`
      }
      return `${mins}м`
    },
    createAttendanceChart() {
      const canvas = this.$refs.attendanceChart
      if (!canvas) return

      const ctx = canvas.getContext('2d')
      const attended = this.attendanceData.attended || 0
      const missed = this.attendanceData.missed || 0
      const cancelled = this.getCancelledCount()

      const total = attended + missed + cancelled
      if (total === 0) return

      const centerX = canvas.width / 2
      const centerY = canvas.height / 2
      const radius = Math.min(centerX, centerY) - 10

      let startAngle = -Math.PI / 2
      const colors = ['#28a745', '#ffc107', '#6c757d']
      const values = [attended, missed, cancelled]

      ctx.clearRect(0, 0, canvas.width, canvas.height)

      values.forEach((value, index) => {
        if (value === 0) return

        const sliceAngle = (value / total) * 2 * Math.PI

        ctx.beginPath()
        ctx.arc(centerX, centerY, radius, startAngle, startAngle + sliceAngle)
        ctx.lineTo(centerX, centerY)
        ctx.fillStyle = colors[index]
        ctx.fill()

        startAngle += sliceAngle
      })
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.createAttendanceChart()
    })
  },
  watch: {
    attendanceData: {
      handler() {
        this.$nextTick(() => {
          this.createAttendanceChart()
        })
      },
      deep: true
    }
  }
}
</script>

<style lang="scss" scoped>
.attendance-details {
  .stats-card {
    border: none;
    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
    transition: transform 0.3s ease;

    &:hover {
      transform: translateY(-2px);
    }
  }

  .attendance-progress {
    .progress {
      height: 10px;
      border-radius: 5px;
    }
  }

  .legend-color {
    width: 12px;
    height: 12px;
    border-radius: 2px;
  }

  .table {
    .lesson-row {
      &:hover {
        background-color: rgba(0, 0, 0, 0.05);
      }
    }

    th {
      font-weight: 600;
      color: #6c757d;
      border-top: none;
    }

    td {
      vertical-align: middle;
    }
  }

  .thead-light th {
    background-color: #f8f9fa;
  }
}

.card {
  border: none;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.dark-layout {
  .attendance-details {
    .stats-card,
    .card {
      background-color: #283046;
      color: #d0d2d6;
    }

    .thead-light th {
      background-color: #404656;
      color: #b4b7bd;
      border-color: #555;
    }

    .table {
      color: #d0d2d6;

      .lesson-row:hover {
        background-color: rgba(255, 255, 255, 0.05);
      }

      td {
        border-color: #555;
      }
    }

    .text-muted {
      color: #b4b7bd !important;
    }
  }
}

@media (max-width: 768px) {
  .attendance-details {
    .table-responsive {
      font-size: 0.9rem;
    }

    .stats-card {
      margin-bottom: 1rem;
    }
  }
}
</style>